import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import styles from "./TimeTracking.module.css";
import { TableLoaderSmall } from "../../../components/LoaderC";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from 'axios';
import { toast } from 'react-smart-toaster';
import { AddLogCallAPI } from "../../../components/AddLogs";
import { Tooltip } from "@mui/material";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const hhmmToDecimal = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    return h + m / 60;
}

const timeToDecimal = (time) => {
    const parsed = dayjs(time, ['HH:mm:ss', 'h:mm A', 'hh:mm A', 'H:mm'], true);

    if (!parsed.isValid()) return null;

    return parsed.hour() + parsed.minute() / 60;
}

const decimalToTime = (decimal) => {
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);

    return dayjs()
        .hour(hours)
        .minute(minutes)
        .second(0)
        .format('h A'); // or 'h:mm A'
}

const formatMinutesToHM = (totalMinutes) => {
    if (totalMinutes === 0)
        return `-`;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;

    return `${hours}h ${minutes}m`;
}

const fmt12 = t => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM"; const hh = h % 12 || 12;
    return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}

const toP = (h, base = 8, span = 14) => Math.max(0, Math.min(100, ((h - base) / span) * 100));

const NewDayModal = ({ setIsModalOpen, selectedEmp, selectedDay, setLoading, updateTimeTracking, editData }) => {
    const { getAccessTokenSilently } = useAuth0();

    const [startTime, setStartTime] = useState("09:00");
    const [endTime, setEndTime] = useState("17:00");

    const [lbStart, setLbStart] = useState("");
    const [lbEnd, setLbEnd] = useState("");
    const [includeLb, setIncludeLb] = useState(true);
    const [errors, setErrors] = useState({});

    function validate() {
        const e = {};
        if (!startTime) e.startTime = "This field is required";
        if (!endTime) e.endTime = "This field is required";
        if (startTime && endTime && endTime <= startTime) e.endTime = "Must be after start time";
        if (includeLb) {
            if (!lbStart) e.lbStart = "This field is required";
            if (!lbEnd) e.lbEnd = "This field is required";
            if (lbStart && lbEnd && lbEnd <= lbStart) e.lbEnd = "Must be after break start";
        }
        return e;
    }

    useEffect(() => {
        if (editData) {
            setStartTime(dayjs(editData?.checkin_time, 'HH:mm:ss').format('HH:mm'));
            setEndTime(dayjs(editData?.checkout_time, 'HH:mm:ss').format('HH:mm'));

            if (editData.break_times) {
                setLbStart(dayjs(editData?.break_times?.lunch_start_time, 'HH:mm:ss').format('HH:mm'));
                setLbEnd(dayjs(editData?.break_times?.lunch_end_time, 'HH:mm:ss').format('HH:mm'));
            }
        } else {
            setStartTime(dayjs(selectedEmp?.checkin_time, 'hh:mm A').format('HH:mm'));
            setEndTime(dayjs(selectedEmp?.checkout_time, 'hh:mm A').format('HH:mm'));

        }
    }, [selectedEmp, selectedDay, editData]);

    const saveTime = async () => {
        const e = validate();
        if (Object.keys(e).length > 0) { setErrors(e); return; }

        let postData = { user_id: selectedEmp?.id, date: selectedDay?.date, checkin_time: dayjs(startTime, 'HH:mm').format('HH:mm:ss'), checkout_time: dayjs(endTime, 'HH:mm').format('HH:mm:ss'), is_lunch_break: includeLb ? 1 : 0 };

        if (includeLb) {
            postData = { ...postData, lunch_start_time: dayjs(lbStart, 'HH:mm').format('HH:mm:ss'), lunch_end_time: dayjs(lbEnd, 'HH:mm').format('HH:mm:ss') };
        }

        setLoading(true);
        setIsModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        return axios.post(process.env.REACT_APP_USER_API_URL + 'v1/time-tracking/add-time', postData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setLoading(false);
            updateTimeTracking(response.data.data);

            let item = response.data.data;

            let logData = {
                'title': 'Time has been added successfully.',
                'description': [
                    'User name: ' + item?.user_name,
                    'Date: ' + dayjs(item?.date).format('MM/DD/YYYY'),
                ]
            }
            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const updateTime = async () => {
        const e = validate();
        if (Object.keys(e).length > 0) { setErrors(e); return; }

        let postData = { user_id: selectedEmp?.id, date: selectedDay?.date, checkin_time: dayjs(startTime, 'HH:mm').format('HH:mm:ss'), checkout_time: dayjs(endTime, 'HH:mm').format('HH:mm:ss'), is_lunch_break: includeLb ? 1 : 0 };

        if (includeLb) {
            postData = { ...postData, lunch_start_time: dayjs(lbStart, 'HH:mm').format('HH:mm:ss'), lunch_end_time: dayjs(lbEnd, 'HH:mm').format('HH:mm:ss') };
        }

        setLoading(true);
        setIsModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        return axios.post(process.env.REACT_APP_USER_API_URL + 'v1/time-tracking/update-time/' + editData?.id, postData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setLoading(false);
            updateTimeTracking(response.data.data);

            let item = response.data.data;

            let logData = {
                'title': 'Time has been update successfully.',
                'description': [
                    'Employee name: ' + item?.user_name,
                    'Date: ' + dayjs(item?.date).format('MM/DD/YYYY'),
                ]
            }
            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const scheduledStart = timeToDecimal(selectedEmp?.checkin_time);
    const scheduledEnd = timeToDecimal(selectedEmp?.checkout_time);
    const actualStart = hhmmToDecimal(startTime);
    const actualEnd = hhmmToDecimal(endTime);

    let regularMinutes = 0;
    let otMinutes = 0;
    let breakMinutes = 0;

    if (actualStart !== null && actualEnd !== null && actualEnd > actualStart) {
        if (includeLb) {
            const lbStartDec = hhmmToDecimal(lbStart);
            const lbEndDec = hhmmToDecimal(lbEnd);
            if (lbStartDec !== null && lbEndDec !== null && lbEndDec > lbStartDec) {
                breakMinutes = Math.round((lbEndDec - lbStartDec) * 60);
            }
        }

        const breakHours = breakMinutes / 60;

        // Net hours actually worked (span minus break)
        const totalWorkedHours = (actualEnd - actualStart) - breakHours;

        // Net scheduled hours (scheduled span minus the same break, since there's
        // no separate "scheduled break" field — the break reduces both sides equally)
        let scheduledNetHours = 0;
        if (scheduledStart !== null && scheduledEnd !== null && scheduledEnd > scheduledStart) {
            scheduledNetHours = Math.max(0, (scheduledEnd - scheduledStart) - breakHours);
        }

        const otHours = Math.max(0, totalWorkedHours - scheduledNetHours);
        const regularHours = Math.min(totalWorkedHours, scheduledNetHours);

        regularMinutes = Math.round(regularHours * 60);
        otMinutes = Math.round(otHours * 60);
    }

    return (<div className={`${styles.modalOverlay}`} onClick={e => { if (e.target === e.currentTarget) setIsModalOpen(false); }}>
        <div className={`${styles.modal}`}>

            <div className={`${styles.modalHead}`}>
                <div className={`${styles.mhLeft}`}>
                    <div className={`${styles.av} ${styles.avPl}`} style={{ width: 34, height: 34, fontSize: 10, flexShrink: 0 }}>
                        {selectedEmp?.user_image_url ? <img src={selectedEmp?.user_image_url} alt="User Avatar" style={{ maxHeight: '28px', maxWidth: '28px' }} /> : selectedEmp?.initial}
                    </div>
                    <div>
                        <div className={`${styles.modalName}`}>{selectedEmp.name}</div>
                        <div className={`${styles.modalDate}`}>{editData ? 'Update time' : 'Add time'} — {dayjs(selectedDay?.date).format('ddd D, MMM YYYY')}</div>
                    </div>
                </div>
                <button className={`${styles.modalClose}`} onClick={(e) => setIsModalOpen(false)} aria-label="Close">✕</button>
            </div>

            <div className={`${styles.modalBody}`}>
                <div className={`${styles.addModalSection}`}>
                    <div className={`${styles.addModalSectionLabel}`}>Shift hours</div>
                    <div className={`${styles.addTimeGrid}`}>
                        <div className={`${styles.addTimeField}`}>
                            <label className={`${styles.addTimeLabel}`}>Start time</label>
                            <input
                                className={`${styles.addTimeInput} ${errors.startTime ? styles.addTimeInputErr : ""}`}
                                type="time"
                                value={startTime}
                                onChange={e => { setStartTime(e.target.value); setErrors(p => ({ ...p, startTime: "" })); }}
                            />
                            {errors.startTime && <span className={`${styles.addTimeErr}`}>{errors.startTime}</span>}
                        </div>
                        <div className={`${styles.addTimeSep}`}>–</div>
                        <div className={`${styles.addTimeField}`}>
                            <label className={`${styles.addTimeLabel}`}>End time</label>
                            <input
                                className={`${styles.addTimeInput} ${errors.endTime ? styles.addTimeInputErr : ""}`}
                                type="time"
                                value={endTime}
                                onChange={e => { setEndTime(e.target.value); setErrors(p => ({ ...p, endTime: "" })); }}
                            />
                            {errors.endTime && <span className={`${styles.addTimeErr}`}>{errors.endTime}</span>}
                        </div>
                    </div>
                </div>

                <div className={`${styles.addModalSection}`}>
                    <div className={`${styles.lbToggleRow}`}>
                        <div className={`${styles.lbToggleLeft}`}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <path d="M18 8h1a4 4 0 0 1 0 8h-1" /><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
                                <line x1="6" y1="1" x2="6" y2="4" /><line x1="10" y1="1" x2="10" y2="4" /><line x1="14" y1="1" x2="14" y2="4" />
                            </svg>
                            <div>
                                <div className={`${styles.addModalSectionLabel}`} style={{ marginBottom: 0 }}>Lunch break</div>
                                <div className={`${styles.lbToggleSub}`}>Did this employee take a lunch break?</div>
                            </div>
                        </div>
                        <button type="button" className={`${styles.toggle} ${includeLb ? styles.toggleOn : ""}`} onClick={() => setIncludeLb(v => !v)} aria-pressed={includeLb} aria-label="Include lunch break">
                            <div className={`${styles.toggleThumb}`} />
                        </button>
                    </div>

                    {includeLb && (<div className={`${styles.addTimeGrid}`} style={{ marginTop: 12 }}>
                        <div className={`${styles.addTimeField}`}>
                            <label className={`${styles.addTimeLabel}`}>Break start</label>
                            <input className={`${styles.addTimeInput} ${errors.lbStart ? styles.addTimeInputErr : ""}`} type="time" value={lbStart} onChange={e => { setLbStart(e.target.value); setErrors(p => ({ ...p, lbStart: "" })); }} />
                            {errors.lbStart && <span className={`${styles.addTimeErr}`}>{errors.lbStart}</span>}
                        </div>
                        <div className={`${styles.addTimeSep}`}>–</div>
                        <div className={`${styles.addTimeField}`}>
                            <label className={`${styles.addTimeLabel}`}>Break end</label>
                            <input className={`${styles.addTimeInput} ${errors.lbEnd ? styles.addTimeInputErr : ""}`} type="time" value={lbEnd} onChange={e => { setLbEnd(e.target.value); setErrors(p => ({ ...p, lbEnd: "" })); }} />
                            {errors.lbEnd && <span className={`${styles.addTimeErr}`}>{errors.lbEnd}</span>}
                        </div>
                    </div>)}
                </div>

                {startTime && endTime && (<div className={`${styles.addSummaryBox}`}>
                    <div className={`${styles.addSummaryRow}`}>
                        <span className={`${styles.addSummaryLbl}`}>Shift</span>
                        <span className={`${styles.addSummaryVal}`}>{fmt12(startTime)} – {fmt12(endTime)}</span>
                    </div>
                    {includeLb && lbStart && lbEnd && (
                        <div className={`${styles.addSummaryRow}`}>
                            <span className={`${styles.addSummaryLbl}`}>Lunch break</span>
                            <span className={`${styles.addSummaryVal}`}>
                                {fmt12(lbStart)} – {fmt12(lbEnd)} ({formatMinutesToHM(breakMinutes)})
                            </span>
                        </div>
                    )}
                    <div className={`${styles.addSummaryRow}`}>
                        <span className={`${styles.addSummaryLbl}`}>Regular hours</span>
                        <span className={`${styles.addSummaryVal}`}>{formatMinutesToHM(regularMinutes)}</span>
                    </div>
                    {otMinutes > 0 && (
                        <div className={`${styles.addSummaryRow}`}>
                            <span className={`${styles.addSummaryLbl}`}>OT hours</span>
                            <span className={`${styles.addSummaryVal} ${styles.addSummaryOt}`}>+{formatMinutesToHM(otMinutes)}</span>
                        </div>
                    )}
                </div>)}
            </div>

            <div className={`${styles.modalFooter}`}>
                <button className={`${styles.btnSecondary}`} onClick={(e) => setIsModalOpen(false)}>Cancel</button>
                {!editData && <button className={`${styles.btnPrimary}`} onClick={(e) => saveTime()}>Add time entry</button>}
                {editData && <button className={`${styles.btnPrimary}`} onClick={(e) => updateTime()}>Update time entry</button>}
            </div>

        </div>
    </div>)
}

const DayModal = ({ setIsModalOpen, selectedEmp, selectedDay, setLoading, updateTimeTracking, deleteTimeTracking, setIsNewModalOpen, setEditData }) => {
    const { getAccessTokenSilently } = useAuth0();

    const [shiftOpen, setShiftOpen] = useState(false);
    const [shiftStart, setShiftStart] = useState(dayjs(selectedDay?.checkin_time, 'HH:mm:ss').format('HH:mm') || "09:00");
    const [shiftEnd, setShiftEnd] = useState(dayjs(selectedDay?.checkout_time, 'HH:mm:ss').format('HH:mm') || "17:00");
    const [saved, setSaved] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const checkinDay = timeToDecimal(selectedDay?.checkin_time);
    const checkinEmp = timeToDecimal(selectedEmp?.checkin_time);

    const checkoutDay = timeToDecimal(selectedDay?.checkout_time);
    const checkoutEmp = timeToDecimal(selectedEmp?.checkout_time);

    let barStartTime = Math.min(checkinDay ?? Infinity, checkinEmp ?? Infinity);

    if (barStartTime === Math.floor(barStartTime)) {
        barStartTime -= 1;
    } else {
        barStartTime = Math.floor(barStartTime);
    }

    let barEndTime = checkoutDay ? Math.max(checkoutDay, checkoutEmp) : checkoutEmp;

    if (barEndTime === Math.floor(barEndTime)) {
        barEndTime += 1;
    } else {
        barEndTime = Math.floor(barEndTime) + 1;
    }

    const barSpan = barEndTime - barStartTime;
    const barMidTime = Math.round(barSpan / 2) + barStartTime;

    const schedL = toP(timeToDecimal(selectedEmp?.checkin_time), barStartTime, barSpan);
    const schedW = toP(timeToDecimal(selectedEmp?.checkout_time), barStartTime, barSpan) - schedL;

    let workedL = 0;
    let workedW = 0;
    let OTLTIME = 0;
    let OTLL = 0;
    let OTLW = 0;
    let OTRTIME = 0;
    let OTRL = 0;
    let OTRW = 0;

    if (checkinEmp > checkinDay) {
        OTLL = toP(timeToDecimal(selectedDay?.checkin_time), barStartTime, barSpan);
        OTLW = toP(timeToDecimal(selectedEmp?.checkin_time), barStartTime, barSpan) - OTLL;

        if (selectedDay?.checkout_time || timeToDecimal(dayjs().tz('America/New_York').format('HH:mm:ss')) > timeToDecimal(selectedEmp?.checkin_time)) {
            OTLW = toP(timeToDecimal(selectedEmp?.checkin_time), barStartTime, barSpan) - OTLL;
            OTLTIME = timeToDecimal(selectedEmp?.checkin_time) * 60 - timeToDecimal(selectedDay?.checkin_time) * 60;
        } else {
            OTLTIME = timeToDecimal(dayjs().tz('America/New_York').format('HH:mm:ss')) * 60 - timeToDecimal(selectedDay?.checkin_time) * 60;
            OTLW = toP(timeToDecimal(dayjs().tz('America/New_York').format('HH:mm:ss')), barStartTime, barSpan) - OTLL;
        }

        workedL = toP(timeToDecimal(selectedEmp?.checkin_time), barStartTime, barSpan);
    } else {
        workedL = toP(timeToDecimal(selectedDay?.checkin_time) || timeToDecimal(selectedEmp?.checkin_time), barStartTime, barSpan);
    }

    if (checkoutDay > checkoutEmp) {
        OTRL = toP(timeToDecimal(selectedEmp?.checkout_time), barStartTime, barSpan);
        OTRW = toP(timeToDecimal(selectedDay?.checkout_time), barStartTime, barSpan) - OTRL;

        OTRTIME = timeToDecimal(selectedDay?.checkout_time) * 60 - timeToDecimal(selectedEmp?.checkout_time) * 60;

        workedW = toP(timeToDecimal(selectedEmp?.checkout_time), barStartTime, barSpan) - workedL;
    } else {
        if (selectedDay?.checkout_time) {
            workedW = toP(timeToDecimal(selectedDay?.checkout_time) || timeToDecimal(selectedEmp?.checkout_time), barStartTime, barSpan) - workedL;
        } else {
            workedW = toP(timeToDecimal(selectedDay?.checkout_time) || timeToDecimal(dayjs().tz('America/New_York').format('HH:mm:ss')), barStartTime, barSpan) - workedL;
        }
    }

    const openShift = () => {
        setShiftOpen(v => !v);
    }

    function handleApply() {
        setSaved(true);
        setTimeout(() => setSaved(false), 1800);
    }

    const declineOT = async (itemId) => {
        setLoading(true);
        setIsModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        return axios.post(process.env.REACT_APP_USER_API_URL + 'v1/time-tracking/ot-declined/' + itemId, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setLoading(false);
            updateTimeTracking(response.data.data);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const approveOT = async (itemId) => {
        setLoading(true);
        setIsModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        return axios.post(process.env.REACT_APP_USER_API_URL + 'v1/time-tracking/ot-approved/' + itemId, { start: dayjs(shiftStart, 'HH:mm').format('HH:mm:ss'), end: dayjs(shiftEnd, 'HH:mm').format('HH:mm:ss') }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setLoading(false);
            updateTimeTracking(response.data.data);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const onUndo = async (itemId) => {
        setLoading(true);
        setIsModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        return axios.post(process.env.REACT_APP_USER_API_URL + 'v1/time-tracking/ot-approved-undo/' + itemId, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setLoading(false);
            updateTimeTracking(response.data.data);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const schedTip = `Scheduled · ${selectedEmp?.checkin_time + " - " + selectedEmp?.checkout_time}`;
    const workedTip = `Worked · ${dayjs(selectedDay?.checkin_time, 'HH:mm:ss').format('hh:mm A')} - ${selectedDay?.checkout_time ? dayjs(selectedDay?.checkout_time, 'HH:mm:ss').format('hh:mm A') : dayjs(selectedDay?.date).isSame(dayjs(), "day") ? 'Now' : 'No out'} (${formatMinutesToHM(selectedDay?.working_time)})`;
    const otTipL = `Overtime · +${formatMinutesToHM(OTLTIME)} prior to scheduled start`;
    const otTipR = `Overtime · +${formatMinutesToHM(OTRTIME)} past scheduled end`;

    return (<div className={`${styles.modalOverlay}`} onClick={e => { if (e.target === e.currentTarget) setIsModalOpen(false); }}>
        <div className={`${styles.modal}`}>

            <div className={`${styles.modalHead}`}>
                <div className={`${styles.mhLeft}`}>
                    <div className={`${styles.av} ${styles.avPl}`} style={{ width: 34, height: 34, fontSize: 10, flexShrink: 0 }}>
                        {selectedEmp?.user_image_url ? <img src={selectedEmp?.user_image_url} alt="User Avatar" style={{ maxHeight: '28px', maxWidth: '28px' }} /> : selectedEmp?.initial}
                    </div>
                    <div>
                        <div className={`${styles.modalName}`}>{selectedEmp.name}</div>
                        <div className={`${styles.modalDate}`}>{dayjs(selectedDay?.date).format('ddd D, MMM YYYY')}</div>
                    </div>
                </div>
                <button className={`${styles.modalClose}`} onClick={(e) => setIsModalOpen(false)} aria-label="Close">✕</button>
            </div>

            <div className={`${styles.timelineWrap}`}>
                <div className={`${styles.tlLabel}`}>Shift timeline</div>
                <div className={`${styles.tlBarWrap}`}>
                    <div className={`${styles.tlTrack}`} />
                    {OTLL > 0 && <Tooltip title={otTipL} arrow placement="bottom">
                        <div className={`${styles.tlOt} ${styles.tlOt1} ${styles.tlSeg}`} style={{ left: `${OTLL}%`, width: `${OTLW}%` }} />
                    </Tooltip>}
                    <Tooltip title={schedTip} arrow placement="bottom">
                        <div className={`${styles.tlSched}`} style={{ left: `${schedL}%`, width: `${schedW}%` }} />
                    </Tooltip>
                    <Tooltip title={workedTip} arrow placement="bottom">
                        <div className={OTRL > 0 && OTLL > 0 ? `${styles.tlWorked}` : OTRL > 0 ? `${styles.tlWorked} ${styles.tlWorked3}` : OTLL > 0 ? `${styles.tlWorked} ${styles.tlWorked2}` : `${styles.tlWorked} ${styles.tlWorked1}`} style={{ left: `${workedL}%`, width: `${workedW}%` }} />
                    </Tooltip>
                    {OTRL > 0 && <Tooltip title={otTipR} arrow placement="bottom">
                        <div className={`${styles.tlOt} ${styles.tlOt2} ${styles.tlSeg}`} style={{ left: `${OTRL}%`, width: `${OTRW}%` }} />
                    </Tooltip>}
                </div>
                <div className={`${styles.tlTimes}`}>
                    <span>{decimalToTime(barStartTime)}</span><span>{decimalToTime(barMidTime)}</span><span>{decimalToTime(barEndTime)}</span>
                </div>
                <div className={`${styles.tlCiLbl}`}>
                    <span className={`${styles.tlBadge} ${styles.tlBadgeIn}`}>{dayjs(selectedDay?.checkin_time, 'HH:mm:ss').format('hh:mm A')}</span>
                    <span className={`${styles.tlArrow}`}>→</span>
                    {selectedDay?.checkout_time ? <span className={`${styles.tlBadge} ${styles.tlBadgeOut}`}>{dayjs(selectedDay?.checkout_time, 'HH:mm:ss').format('hh:mm A')}</span> : dayjs(selectedDay?.date).isSame(dayjs(), "day") ? <span className={`${styles.tlBadge} ${styles.tlBadgeProg}`}>Now</span> : <span className={`${styles.tlBadge} ${styles.tlBadgeMiss}`}>No out</span>}
                    <span className={`${styles.tlDur}`}>{formatMinutesToHM(selectedDay?.working_time)} Worked</span>
                </div>
            </div>

            <div className={`${styles.modalBody}`}>
                <div className={`${styles.infoRow}`}>
                    <span className={`${styles.irLbl}`}>⏱ Scheduled shift</span>
                    <span className={`${styles.irVal}`}>{selectedEmp?.checkin_time + " - " + selectedEmp?.checkout_time}</span>
                </div>
                <div className={`${styles.infoRow}`}>
                    <span className={`${styles.irLbl}`}>◎ Shift target</span>
                    <span className={`${styles.irVal}`}>{formatMinutesToHM(selectedDay?.shift_minutes)}</span>
                </div>
                <div className={`${styles.infoRow}`}>
                    <span className={`${styles.irLbl}`}>☕ Break Times</span>
                    <span className={`${styles.irVal}`}>{formatMinutesToHM(selectedDay?.break_mintes)}</span>
                </div>
                <div className={`${styles.infoRow} ${styles.infoRowLast}`}>
                    <span className={`${styles.irLbl}`}>⌛ Total worked</span>
                    <span className={selectedDay?.is_ot_approved > 0 ? `${styles.irVal} ${styles.irOt}` : selectedDay?.late ? `${styles.irVal} ${styles.irLate}` : `${styles.irVal} ${styles.irOk}`}>{formatMinutesToHM(selectedDay?.working_time)}</span>
                </div>

                {selectedDay?.is_ot_approved === 1 && (<>
                    <div className={`${styles.otBox}`}>
                        <div className={`${styles.otBoxTitle}`}>⚠ Overtime detected</div>
                        <div className={`${styles.otAmount}`}>+{formatMinutesToHM(selectedDay?.ot_time)} overtime</div>
                        <div className={`${styles.otDesc}`}>
                            Worked {formatMinutesToHM(selectedDay?.ot_time)} beyond the {formatMinutesToHM(selectedDay?.shift_minutes)} scheduled shift.
                            Approve to confirm paid overtime, or decline to mark as unpaid.
                        </div>
                    </div>

                    <div className={`${styles.shiftSection}`}>
                        <button className={`${styles.shiftToggleBtn}`} onClick={openShift}>
                            ✏ {shiftOpen ? "Hide shift editor" : "Adjust shift hours (optional)"}
                        </button>
                        {shiftOpen && (
                            <div className={`${styles.shiftEditBody}`}>
                                <div className={`${styles.seLbl}`}>Override shift hours</div>
                                <div className={`${styles.shiftInputs}`}>
                                    <input className={`${styles.shiftInput}`} type="time" value={shiftStart} onChange={e => setShiftStart(e.target.value)} aria-label="Shift start" />
                                    <span className={`${styles.shiftSep}`}>–</span>
                                    <input className={`${styles.shiftInput}`} type="time" value={shiftEnd} onChange={e => setShiftEnd(e.target.value)} aria-label="Shift end" />
                                    <button className={`${styles.applyBtn} ${saved ? styles.applyBtnSaved : ""}`} onClick={handleApply}>
                                        {saved ? "Saved ✓" : "Apply"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </>)}

                {(selectedDay?.is_ot_approved === 2 || selectedDay?.is_ot_approved === 3) && (<div className={`${styles.otBox} ${selectedDay?.is_ot_approved === 3 ? styles.otBoxDeclined : styles.otBoxApproved}`}>
                    <div className={`${styles.otBoxTitle}`}>
                        {selectedDay?.is_ot_approved === 2 ? "✓ Overtime approved" : "✕ Overtime declined"}
                    </div>
                    <div className={`${styles.otAmount}`}>+{formatMinutesToHM(selectedDay?.ot_time)} overtime</div>
                    <div className={`${styles.otDesc}`}>
                        {selectedDay?.is_ot_approved === 2 ? `Confirmed as paid overtime` : `Marked as unpaid overtime`}
                    </div>
                    <div className={`${styles.otResolvedActions}`}>
                        <button className={`${styles.otActionBtn} ${styles.otActionBtnUndo}`} onClick={() => onUndo(selectedDay?.id)}>
                            ↺ Undo
                        </button>
                    </div>
                </div>)}

            </div>

            <div className={`${styles.modalFooter}`}>
                {!confirmDelete ? (<div className={`${styles.footerLeft}`}>
                    <button className={`${styles.btnIconEdit}`} onClick={() => { setIsModalOpen(false); setIsNewModalOpen(true); setEditData(selectedDay) }} title="Edit this time record">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Edit
                    </button>
                    <button className={`${styles.btnIconDanger}`} onClick={() => setConfirmDelete(true)} title="Delete this time record">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" /><path d="M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                        Delete
                    </button>
                </div>) : (<div className={`${styles.deleteConfirmRow}`}>
                    <span className={`${styles.deleteConfirmMsg}`}>Delete this record?</span>
                    <button className={`${styles.btnDanger}`} onClick={() => { setIsModalOpen(false); deleteTimeTracking(selectedDay?.id); }}>Yes, delete</button>
                    <button className={`${styles.btnSecondary}`} onClick={() => setConfirmDelete(false)}>Cancel</button>
                </div>)}


                {!confirmDelete && (<div className={`${styles.footerRight}`}>
                    {selectedDay?.is_ot_approved > 1 && <>
                        <span className={`${styles.footerStatus} ${selectedDay?.is_ot_approved === 2 ? styles.footerOk : styles.footerNo}`}>
                            {selectedDay?.is_ot_approved === 2 && "✓ OT Approved"}
                            {selectedDay?.is_ot_approved === 3 && "✕ OT Declined"}
                        </span>
                        <button className={`${styles.btnSecondary}`} onClick={(e) => setIsModalOpen(false)}>Close</button>
                    </>}

                    {selectedDay?.is_ot_approved === 1 && <>
                        <button className={`${styles.btnDanger}`} onClick={(e) => declineOT(selectedDay?.id)}>Decline OT</button>
                        <button className={`${styles.btnPrimary}`} onClick={(e) => approveOT(selectedDay?.id)}>✓ Approve OT</button>
                    </>}

                    {selectedDay?.is_ot_approved === 0 && <button className={`${styles.btnSecondary}`} onClick={(e) => setIsModalOpen(false)}>Close</button>}
                </div>)}
            </div>

        </div>
    </div >
    );
}

function DayViewRow({ item, index, setIsModalOpen, setIsNewModalOpen, setSelectedEmp, setSelectedDay, setEditData }) {
    let classN = `${styles.av}`;

    if (index % 5 === 0) {
        classN = `${styles.av} ${styles.avPl}`;
    }
    if (index % 5 === 1) {
        classN = `${styles.av} ${styles.avTw}`;
    }
    if (index % 5 === 2) {
        classN = `${styles.av} ${styles.avCh}`;
    }
    if (index % 5 === 3) {
        classN = `${styles.av} ${styles.avMj}`;
    }
    if (index % 5 === 4) {
        classN = `${styles.av} ${styles.avRl}`;
    }

    let timeTracking = item?.timeTracking[0];

    if (item?.working_days_arr.includes(dayjs().format("ddd")) && dayjs(timeTracking?.date).isSame(dayjs(), "day") && !timeTracking?.checkin_time)
        return (<tr className={`${styles.tbodyTr}`} style={{ cursor: "pointer" }} onClick={(e) => { setIsNewModalOpen(true); setSelectedEmp(item); setSelectedDay(timeTracking); setEditData(null); }}>
            <td className={`${styles.tdEc}`}>
                <div className={`${styles.empWrap}`}>
                    <div className={`${classN}`}>{item?.user_image_url ? <img src={item?.user_image_url} alt="User Avatar" style={{ maxHeight: '28px', maxWidth: '28px' }} /> : item?.initial}</div>
                    <div>
                        <div className={`${styles.empName}`}>{item?.name}</div>
                        <div className={`${styles.empRole}`}>{item?.role}</div>
                    </div>
                </div>
            </td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.dvStatusPending}`}>Pending</span></td>
        </tr>);

    if (!item?.working_days_arr.includes(dayjs().format("ddd")) || !timeTracking?.checkin_time)
        return (<tr className={`${styles.tbodyTr}`} style={{ cursor: "pointer" }} onClick={(e) => { setIsNewModalOpen(true); setSelectedEmp(item); setSelectedDay(timeTracking); setEditData(null); }}>
            <td className={`${styles.tdEc}`}>
                <div className={`${styles.empWrap}`}>
                    <div className={`${classN}`}>{item?.user_image_url ? <img src={item?.user_image_url} alt="User Avatar" style={{ maxHeight: '28px', maxWidth: '28px' }} /> : item?.initial}</div>
                    <div>
                        <div className={`${styles.empName}`}>{item?.name}</div>
                        <div className={`${styles.empRole}`}>{item?.role}</div>
                    </div>
                </div>
            </td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.dh}`}>—</span></td>
            <td className={`${styles.td}`}><span className={`${styles.offLbl}`}>Day off</span></td>
        </tr>);

    const statusEl = !timeTracking?.checkout_time ? <span className={`${styles.dvStatusProg}`}>In progress</span> : timeTracking?.is_ot_approved === 2 ? <span className={`${styles.dvStatusOk}`}>OT approved</span> : timeTracking?.is_ot_approved === 3 ? <span className={`${styles.dvStatusOff}`}>OT declined</span> : timeTracking?.is_ot_approved === 1 ? <span className={`${styles.dvStatusOt}`}>Overtime ⚠</span> : item?.late ? <span className={`${styles.dvStatusLate}`}>Late start</span> : <span className={`${styles.dvStatusOk}`}>Complete</span>;

    let wageTotal = 0;

    if (item?.wage > 0 && timeTracking?.regular_minutes > 0) {
        wageTotal += (timeTracking?.regular_minutes / 60) * item?.wage;
    }

    if (item?.ot_wage > 0 && timeTracking?.approved_ot_time > 0) {
        wageTotal += (timeTracking?.approved_ot_time / 60) * item?.ot_wage;
    }

    wageTotal = Math.round(wageTotal);

    return (<tr className={`${styles.tbodyTr}`} style={{ cursor: "pointer" }} onClick={(e) => { setIsModalOpen(true); setSelectedEmp(item); setSelectedDay(timeTracking); }}>
        <td className={`${styles.tdEc}`}>
            <div className={`${styles.empWrap}`}>
                <div className={`${classN}`}>{item?.user_image_url ? <img src={item?.user_image_url} alt="User Avatar" style={{ maxHeight: '28px', maxWidth: '28px' }} /> : item?.initial}</div>
                <div>
                    <div className={`${styles.empName}`}>{item?.name}</div>
                    <div className={`${styles.empRole}`}>{item?.role}</div>
                </div>
            </div>
        </td>

        <td className={`${styles.td}`}>
            <span className={`${styles.dh} ${timeTracking?.late ? styles.dvTimeLate : ""}`}>{dayjs(timeTracking?.checkin_time, 'HH:mm:ss').format('hh:mm A')}</span>
        </td>

        <td className={`${styles.td}`}>{timeTracking?.checkout_time ? <span className={`${styles.dh}`}>{dayjs(timeTracking?.checkout_time, 'HH:mm:ss').format('hh:mm A')}</span> : dayjs(timeTracking?.date).isSame(dayjs(), "day") ? <span className={`${styles.dvInProgLbl}`}>in progress</span> : <span className={`${styles.dvInMissingLbl}`}>missing</span>}</td>

        <td className={`${styles.td}`}>
            {timeTracking?.break_mintes > 0 ? <span className={`${styles.dh}`}>{formatMinutesToHM(timeTracking?.break_mintes)}</span> : <span className={`${styles.dh}`}>—</span>}
        </td>

        <td className={`${styles.td}`}>
            {timeTracking?.regular_minutes > 0 ? <span className={`${styles.dh}`}>{formatMinutesToHM(timeTracking?.regular_minutes)}</span> : <span className={`${styles.dh}`}>—</span>}
        </td>

        <td className={`${styles.td}`}>
            {timeTracking?.approved_ot_time > 0 ? <span className={`${styles.dh}`}>{formatMinutesToHM(timeTracking?.approved_ot_time)}</span> : <span className={`${styles.dh}`}>—</span>}
        </td>

        <td className={`${styles.td}`}>
            {wageTotal > 0 ? <span className={`${styles.dh}`}>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(wageTotal)}</span> : <span className={`${styles.dh}`}>—</span>}
        </td>

        <td className={`${styles.td}`}>{statusEl}</td>
    </tr>);
}

const DayViewTable = ({ contentLoading, filteredData, setLoading, updateTimeTracking, deleteTimeTracking }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isNewModalOpen, setIsNewModalOpen] = useState(false);
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [editData, setEditData] = useState(null);

    return (<div className={`${styles.tblWrap}`}>
        {contentLoading && <div className="Loader TableLoader"><TableLoaderSmall /></div>}

        {!contentLoading && filteredData?.length > 0 && <table className={`${styles.table}`}>
            <thead>
                <tr className={`${styles.dvThead}`}>
                    <th className={`${styles.thEc}`}>Employee</th>
                    <th className={`${styles.th}`}>Clock in</th>
                    <th className={`${styles.th}`}>Clock out</th>
                    <th className={`${styles.th}`}>Lunch Break</th>
                    <th className={`${styles.th}`}>Reg. Hrs</th>
                    <th className={`${styles.th}`}>OT Hrs</th>
                    <th className={`${styles.th}`}>Wage</th>
                    <th className={`${styles.th}`}>Status</th>
                </tr>
            </thead>
            <tbody>
                {filteredData.map((item, index) => (
                    <DayViewRow key={item?.id} index={index} item={item} setIsModalOpen={setIsModalOpen.bind(this)} setIsNewModalOpen={setIsNewModalOpen.bind(this)} setSelectedEmp={setSelectedEmp.bind(this)} setSelectedDay={setSelectedDay.bind(this)} setEditData={setEditData.bind(this)} />
                ))}
            </tbody>
        </table>}

        {filteredData?.length === 0 && <>
            <div className={`${styles.hrBody}`}>
                <div className={`${styles.panel} ${styles.panelEmpty}`}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: "#ccc", marginBottom: 10 }} aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                        <line x1="4" y1="4" x2="20" y2="20" />
                    </svg>
                    <div className={`${styles.emptyTitle}`}>Time tracking records unavailable.</div>
                    <div className={`${styles.emptySub}`}>Time entries will appear here once tracking starts.</div>
                </div>
            </div>
        </>}


        {isModalOpen && (<DayModal setIsModalOpen={setIsModalOpen.bind(this)} selectedEmp={selectedEmp} selectedDay={selectedDay} setLoading={setLoading.bind(this)} updateTimeTracking={updateTimeTracking.bind(this)} deleteTimeTracking={deleteTimeTracking.bind(this)} setIsNewModalOpen={setIsNewModalOpen.bind(true)} setEditData={setEditData.bind(this)} />)}

        {isNewModalOpen && (<NewDayModal setIsModalOpen={setIsNewModalOpen.bind(this)} selectedEmp={selectedEmp} selectedDay={selectedDay} setLoading={setLoading.bind(this)} updateTimeTracking={updateTimeTracking.bind(this)} editData={editData} />)}
    </div>);
}

export default DayViewTable;